import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";

export const InquiriesManagement = () => {
  const { token } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);

  const sendTestAlert = async () => {
    setSendingTest(true);
    try {
      const res = await axios.post(`${API}/admin/inquiries/test-alert`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Test alert sent to ${res.data.sent_to}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test alert");
    } finally {
      setSendingTest(false);
    }
  };

  const fetchInquiries = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInquiries(response.data);
    } catch (error) {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}/status?new_status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status updated");
      fetchInquiries();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div data-testid="admin-inquiries">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl text-charcoal mb-2">Booking Inquiries</h1>
          <p className="font-body text-stone-grey">Review and manage booking requests. New inquiries are emailed to you instantly.</p>
        </div>
        <Button variant="outline" onClick={sendTestAlert} disabled={sendingTest} data-testid="inquiries-test-alert-btn">
          <Send className="w-4 h-4 mr-2" /> {sendingTest ? "Sending..." : "Send test alert"}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      ) : inquiries.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <Mail className="w-12 h-12 text-taupe mx-auto mb-4" />
          <p className="font-body text-stone-grey">No inquiries yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {inquiries.map((inquiry) => (
            <div key={inquiry.id} className="glass rounded-xl p-6">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="font-heading text-xl text-charcoal">{inquiry.name}</h3>
                </div>
                <span className={`badge ${
                  inquiry.status === 'pending' ? 'badge-pending' :
                  inquiry.status === 'approved' ? 'badge-approved' : 'badge-declined'
                }`}>
                  {inquiry.status}
                </span>
              </div>

              {/* Contact Information - IMPORTANT */}
              <div className="bg-jade-soft/20 rounded-lg p-4 mb-4 border border-jade-soft/30">
                <h4 className="font-heading text-sm text-jade-deep mb-3 uppercase tracking-wide">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {inquiry.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-jade-text" />
                      <a href={`mailto:${inquiry.email}`} className="font-body text-charcoal hover:text-jade-deep transition-colors">
                        {inquiry.email}
                      </a>
                    </div>
                  )}
                  {inquiry.phone && (
                    <div className="flex items-center gap-2">
                      <span className="text-jade-text">📞</span>
                      <a href={`tel:${inquiry.phone}`} className="font-body text-charcoal hover:text-jade-deep transition-colors">
                        {inquiry.phone}
                      </a>
                    </div>
                  )}
                  {!inquiry.email && !inquiry.phone && (
                    <p className="font-body text-taupe text-sm italic">No contact info provided</p>
                  )}
                </div>
              </div>
              
              {/* Booking Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                <div>
                  <span className="font-body text-taupe block">Preferred City:</span>
                  <p className="font-body text-charcoal">{inquiry.preferred_city || 'Not specified'}</p>
                </div>
                <div>
                  <span className="font-body text-taupe block">Duration:</span>
                  <p className="font-body text-charcoal">{inquiry.desired_duration || 'Not specified'}</p>
                </div>
                <div>
                  <span className="font-body text-taupe block">Preferred Date:</span>
                  <p className="font-body text-charcoal">{inquiry.preferred_date || 'Flexible'}</p>
                </div>
                <div>
                  <span className="font-body text-taupe block">Submitted:</span>
                  <p className="font-body text-charcoal">{formatDate(inquiry.created_at)}</p>
                </div>
              </div>

              {/* Screening & Referral */}
              {(inquiry.screening_method || inquiry.referral_source) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                  {inquiry.screening_method && (
                    <div>
                      <span className="font-body text-taupe block">Screening Method:</span>
                      <p className="font-body text-charcoal">{inquiry.screening_method}</p>
                    </div>
                  )}
                  {inquiry.referral_source && (
                    <div>
                      <span className="font-body text-taupe block">How they found you:</span>
                      <p className="font-body text-charcoal">{inquiry.referral_source}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4">
                <span className="font-body text-taupe text-sm block mb-1">Message:</span>
                <p className="font-body text-stone-grey bg-sand/20 rounded-lg p-3">{inquiry.message}</p>
              </div>

              <div className="flex gap-2">
                <Button size="sm" onClick={() => updateStatus(inquiry.id, 'approved')} className="bg-jade-deep text-white text-xs">Approve</Button>
                <Button size="sm" variant="outline" onClick={() => updateStatus(inquiry.id, 'declined')} className="text-xs">Decline</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

